import React, { useState, useRef, useCallback, useEffect } from 'react';
import './CurriculumPlanGenerator.css';
import { API_BASE_URL } from '../config';
import CurriculumPlanTable from './CurriculumPlanTable';

// Backend gerçek bir ilerleme yüzdesi döndürmüyor (tek senkron istekte 1-3 ardışık
// Gemini çağrısı yapılıyor). Bu yüzden süreye dayalı, tavana yaklaştıkça yavaşlayan
// bir tahmini ilerleme simüle ediyoruz; gerçek yanıt gelince anında %100'e tamamlanır.
export function useSimulatedProgress() {
  const [percent, setPercent] = useState(0);
  const intervalRef = useRef(null);

  const clear = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const start = useCallback((estimatedMs) => {
    clear();
    setPercent(0);
    const startTime = Date.now();
    const cap = 96;
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed <= estimatedMs) {
        const ratio = elapsed / estimatedMs;
        const eased = 1 - Math.pow(1 - ratio, 3);
        setPercent(Math.min(eased * 90, 90));
      } else {
        // Tahmini süre aşıldı: AI yanıtı gelene kadar %90-96 arasında
        // yavaşça ilerlemeye devam et, tavana çarpıp donmuş görünmesin.
        const overtime = elapsed - estimatedMs;
        const overtimeRatio = Math.min(overtime / (estimatedMs * 1.5), 1);
        const eased = 1 - Math.pow(1 - overtimeRatio, 2);
        setPercent(90 + eased * (cap - 90));
      }
    }, 200);
  }, []);

  const complete = useCallback(() => {
    clear();
    setPercent(100);
  }, []);

  const reset = useCallback(() => {
    clear();
    setPercent(0);
  }, []);

  return { percent, start, complete, reset };
}

export function ProgressBar({ label, percent }) {
  const phase = percent < 30
    ? 'Veriler hazırlanıyor'
    : percent < 65
      ? 'Ölçütler değerlendiriliyor'
      : percent < 90
        ? 'Sonuç hazırlanıyor'
        : 'Sonuç bekleniyor';
  const displayedPercent = Math.min(100, Math.round(percent));
  const progressText = `%${displayedPercent} · ${phase}`;

  return (
    <div className="progress-bar-container" style={{ width: '100%', maxWidth: 480 }}>
      <div className="progress-label">
        <span>{label}</span>
        <span>{progressText}</span>
      </div>
      <div
        className="progress-bar"
        role="progressbar"
        aria-label={label}
        aria-valuenow={displayedPercent}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuetext={phase}
      >
        <div className="progress-fill" style={{ width: `${Math.max(6, Math.min(100, percent))}%` }} />
      </div>
    </div>
  );
}

const CURRICULUM_GENERATION_STEPS = [
  { id: 'research', label: 'Alan araştırması' },
  { id: 'generation', label: 'Plan oluşturma' },
  { id: 'validation', label: 'Doğrulama' },
];

const CURRICULUM_STAGE_PROGRESS = {
  preparing: { start: 4, cap: 10, speed: 0.08, minStep: 0.4 },
  research: { start: 12, cap: 35, speed: 0.04, minStep: 0.35 },
  generation: { start: 38, cap: 89, speed: 0.015, minStep: 0.22 },
  validation: { start: 91, cap: 99, speed: 0.035, minStep: 0.18 },
  completed: { start: 100, cap: 100, speed: 1, minStep: 1 },
};

function createProgressRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function CurriculumGenerationProgress({ progressState }) {
  const stage = progressState?.stage || 'preparing';
  const stageProgress = CURRICULUM_STAGE_PROGRESS[stage] || CURRICULUM_STAGE_PROGRESS.preparing;
  const [progressPercent, setProgressPercent] = useState(stageProgress.start);
  const activeIndex = stage === 'preparing'
    ? 0
    : CURRICULUM_GENERATION_STEPS.findIndex((step) => step.id === stage);
  const isCompleted = stage === 'completed';

  useEffect(() => {
    if (stage === 'completed') {
      setProgressPercent(100);
      return undefined;
    }

    setProgressPercent((current) => Math.max(current, stageProgress.start));
    const timer = window.setInterval(() => {
      setProgressPercent((current) => {
        if (current >= stageProgress.cap) return stageProgress.cap;
        const remaining = stageProgress.cap - current;
        const increment = Math.max(stageProgress.minStep, remaining * stageProgress.speed);
        return Math.min(stageProgress.cap, current + increment);
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [stage, stageProgress]);

  const displayedPercent = Math.round(progressPercent);

  return (
    <div className="curriculum-generation-progress" role="status" aria-live="polite">
      <div className="curriculum-generation-progress__current">
        <span className="plan-generator__loading-pulse" aria-hidden="true" />
        {progressState?.label || 'Veriler hazırlanıyor'}
      </div>
      <ol className="curriculum-generation-progress__steps">
        {CURRICULUM_GENERATION_STEPS.map((step, index) => {
          const completed = isCompleted || index < activeIndex;
          const active = !isCompleted && index === activeIndex;
          return (
            <li
              key={step.id}
              className={`curriculum-generation-progress__step${completed ? ' curriculum-generation-progress__step--completed' : ''}${active ? ' curriculum-generation-progress__step--active' : ''}`}
            >
              <span className="curriculum-generation-progress__marker" aria-hidden="true">
                {completed ? '✓' : index + 1}
              </span>
              <span>{step.label}</span>
            </li>
          );
        })}
      </ol>
      <div className="curriculum-generation-progress__bar-row">
        <div
          className="curriculum-generation-progress__bar"
          role="progressbar"
          aria-label="Ders planı oluşturma ilerlemesi"
          aria-valuenow={displayedPercent}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            className="curriculum-generation-progress__bar-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <strong>%{displayedPercent}</strong>
      </div>
    </div>
  );
}

function CurriculumPlanGenerator({
  programId, userType, programAdminToken, adminToken, onGoToOutcomes,
  initialProgramOutcomes = [], initialExtraInstructions = '', autoGenerate = false,
}) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'loading' | 'result' | 'error'
  const [error, setError] = useState('');
  const [planData, setPlanData] = useState(null);
  const [requiresOutcomes, setRequiresOutcomes] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState(initialExtraInstructions);
  const [generationProgress, setGenerationProgress] = useState({
    stage: 'preparing',
    label: 'Veriler hazırlanıyor',
  });
  const autoGenerationStarted = useRef(false);

  const buildAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (userType === 'programAdmin' && programAdminToken) {
      headers.Authorization = `Bearer ${programAdminToken}`;
    } else if (adminToken) {
      headers.Authorization = `Bearer ${adminToken}`;
    }
    return headers;
  };

  const handleGeneratePlan = async () => {
    setMode('loading');
    setError('');
    setRequiresOutcomes(false);
    setGenerationProgress({ stage: 'preparing', label: 'Veriler hazırlanıyor' });
    const progressRequestId = createProgressRequestId();
    const pollProgress = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/program/${programId}/ders-plani/oneri/durum/${progressRequestId}`,
          { headers: buildAuthHeaders() },
        );
        if (!response.ok) return;
        const status = await response.json();
        if (status?.stage) {
          setGenerationProgress({
            stage: status.stage,
            label: status.label || 'İşlem devam ediyor',
          });
        }
      } catch (error) {
        // İlerleme sorgusu best-effort çalışır; ana üretim isteğini etkilemez.
      }
    };
    const progressPollTimer = window.setInterval(pollProgress, 900);
    pollProgress();
    try {
      const res = await fetch(`${API_BASE_URL}/api/program/${programId}/ders-plani/oneri`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({
          progress_request_id: progressRequestId,
          extra_instructions: extraInstructions.trim(),
          ...(initialProgramOutcomes.length > 0
            ? { program_outcomes: initialProgramOutcomes }
            : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError((json && json.error) || 'Plan oluşturulamadı, tekrar deneyin');
        setRequiresOutcomes(json && json.code === 'PROGRAM_OUTCOMES_REQUIRED');
        setMode('error');
        return;
      }
      setGenerationProgress({ stage: 'completed', label: 'Ders planı hazır' });
      setPlanData(json);
      setMode('result');
    } catch (err) {
      setError('Plan oluşturulamadı, tekrar deneyin');
      setMode('error');
    } finally {
      window.clearInterval(progressPollTimer);
    }
  };

  useEffect(() => {
    if (!autoGenerate || !programId || initialProgramOutcomes.length === 0 || autoGenerationStarted.current) {
      return;
    }
    autoGenerationStarted.current = true;
    handleGeneratePlan();
    // Otomatik üretim yalnızca çıktı ekranından bu sayfaya ilk geçişte bir kez başlatılır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, programId]);

  if (mode === 'idle') {
    return (
      <div className="plan-generator__start">
        <div className="plan-generator__extra">
          {initialProgramOutcomes.length > 0 && (
            <div className="plan-generator__approved-outcomes-note">
              Seçilen {initialProgramOutcomes.length} program çıktısına göre ders planı önerisi hazırlanacaktır.
              Listeyi değiştirmek için Program Çıktıları ekranına dönebilirsiniz.
            </div>
          )}
          <label htmlFor="curriculum-extra-instructions">Ek talimat (opsiyonel)</label>
          <textarea
            id="curriculum-extra-instructions"
            value={extraInstructions}
            onChange={(event) => setExtraInstructions(event.target.value)}
            placeholder="Yapay zekaya ders planını oluştururken dikkate alması için ek talimat yazın (ör. belirli bir dersin korunması, belirli bir yetkinliğe ağırlık verilmesi vb.)"
            rows={3}
            maxLength={2000}
          />
          <div className="plan-generator__start-actions">
            <button
              type="button"
              onClick={handleGeneratePlan}
              disabled={!programId}
              className="button plan-generator__start-button"
            >
              Ders Planı Oluştur
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'loading') {
    return (
      <div className="plan-generator__loading" aria-live="polite">
        <div className="plan-generator__loading-status">
          <span className="plan-generator__loading-pulse" aria-hidden="true" />
          Yapay zekâ değerlendirmesi devam ediyor
        </div>
        <CurriculumGenerationProgress progressState={generationProgress} />
        <p className="plan-generator__loading-note">
          {initialProgramOutcomes.length > 0
            ? `Nihai listeden aktarılan ${initialProgramOutcomes.length} geçici program çıktısı; `
            : 'Program çıktıları; '}
          mevcut ders içerikleri, AKTS dağılımı, alana özgü Türkiye/dünya eğitim esasları ve
          uygun emsal programlarla birlikte inceleniyor. Bu işlem birkaç dakika sürebilir.
        </p>
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div className="plan-generator__error">
        <div className="plan-generator__error-message">{error}</div>
        {requiresOutcomes && onGoToOutcomes ? (
          <button onClick={onGoToOutcomes} className="button plan-generator__retry">
            Program Çıktılarını Güncelle
          </button>
        ) : (
          <button onClick={handleGeneratePlan} className="button plan-generator__retry">
            Tekrar Dene
          </button>
        )}
      </div>
    );
  }

  if (mode === 'result' && planData) {
    return (
      <CurriculumPlanTable
        programId={programId}
        programName={planData.program_name}
        durationYear={planData.duration_year}
        eskiPlan={planData.eski_plan}
        initialYeniPlan={planData.yeni_plan}
        kaldirilanlar={planData.kaldirilanlar}
        programOutcomes={planData.program_ciktilari || initialProgramOutcomes}
        userType={userType}
        programAdminToken={programAdminToken}
        adminToken={adminToken}
        onRegenerate={handleGeneratePlan}
      />
    );
  }

  return null;
}

export default CurriculumPlanGenerator;
